import { useSyncExternalStore } from 'react';

/**
 * What the top-bar Ask Moo button needs to know about the panel it opens:
 * whether the panel is open, and how many Moo tasks are running across all
 * conversations.
 *
 * The button lives in the console header, outside the Moo provider tree, and
 * only dispatches MOO_V2_TOGGLE_EVENT. The integration publishes its state
 * here and the button subscribes. Backed by a globalThis singleton for the
 * same reason as `cmail-selection-store`: one instance across duplicate
 * module evaluations under the test runner.
 */
const KEY = '__cowbellMooLauncherStatusStore__';

export interface LauncherStatus {
  readonly isOpen: boolean;
  readonly runningCount: number;
}

interface Store {
  status: LauncherStatus;
  listeners: Set<() => void>;
}

const g = globalThis as unknown as Record<string, Store | undefined>;
const store: Store = (g[KEY] ??= {
  status: { isOpen: false, runningCount: 0 },
  listeners: new Set(),
});

export function publishLauncherStatus(next: LauncherStatus): void {
  const current = store.status;
  if (
    current.isOpen === next.isOpen &&
    current.runningCount === next.runningCount
  ) {
    return;
  }
  store.status = next;
  store.listeners.forEach((listener) => listener());
}

function subscribe(listener: () => void): () => void {
  store.listeners.add(listener);
  return () => {
    store.listeners.delete(listener);
  };
}

function getStatus(): LauncherStatus {
  return store.status;
}

export function useLauncherStatus(): LauncherStatus {
  return useSyncExternalStore(subscribe, getStatus, getStatus);
}
